// src/admin/StudentManager.js - Tự động lưu lịch sử chuyển lớp khi đổi lớp
import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import './StudentManager.css';

const StudentManager = () => {
  const navigate = useNavigate();
  const { selectedClassForOutlet } = useOutletContext();

  const [studentsList, setStudentsList] = useState([]);
  const [currentYearClasses, setCurrentYearClasses] = useState([]);
  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [loading, setLoading] = useState(true);

  // State Niên Khóa & Điểm Trung Bình Cả Năm
  const [academicYearsList, setAcademicYearsList] = useState([]);
  const [selectedScoreYearId, setSelectedScoreYearId] = useState('');
  const [scoresMap, setScoresMap] = useState({});
  const [loadingScores, setLoadingScores] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdatingClass, setIsUpdatingClass] = useState({});
  const [isUpdatingStatus, setIsUpdatingStatus] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const statusOptions = [
    { value: 'Còn đi học', label: 'Còn đi học' },
    { value: 'Đang học', label: 'Đang học' },
    { value: 'Nghỉ luôn', label: 'Nghỉ luôn' },
    { value: 'Tốt nghiệp', label: 'Tốt nghiệp' },
    { value: 'Chuyển xứ', label: 'Chuyển xứ' }
  ];

  // 1. TẢI DỮ LIỆU BAN ĐẦU
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const allYearsQuery = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const allYearsSnap = await getDocs(allYearsQuery);
        const yearsList = allYearsSnap.docs.map(d => ({
          id: d.id,
          name: d.data().name || d.id,
          isCurrent: d.data().isCurrent || false
        }));
        setAcademicYearsList(yearsList);

        let currentYearDoc = allYearsSnap.docs.find(d => d.data().isCurrent === true);
        if (!currentYearDoc && allYearsSnap.docs.length > 0) {
          currentYearDoc = allYearsSnap.docs[0];
        }

        if (!currentYearDoc) {
          setStudentsList([]);
          setCurrentYearClasses([]);
          setLoading(false);
          return;
        }

        const yId = currentYearDoc.id;
        const yData = currentYearDoc.data();
        setCurrentYearId(yId);
        setCurrentYearName(yData.name || yId);
        setSelectedScoreYearId(yId);

        const gradesSnap = await getDocs(collection(firestore, `academic_years/${yId}/grades`));
        const gradeOrderMap = {};
        gradesSnap.docs.forEach(d => {
          gradeOrderMap[d.id] = d.data().sortOrder || 0;
        });

        const classesSnap = await getDocs(collection(firestore, `academic_years/${yId}/classes`));
        const classesList = classesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        classesList.sort((a, b) => {
          const orderA = gradeOrderMap[a.gradeId] ?? 999;
          const orderB = gradeOrderMap[b.gradeId] ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });

        setCurrentYearClasses(classesList);

        const enrollmentsSnap = await getDocs(collection(firestore, `academic_years/${yId}/enrollments`));
        const enrollmentsMap = {};
        enrollmentsSnap.docs.forEach(d => {
          const data = d.data();
          enrollmentsMap[data.studentId || d.id] = data;
        });

        const studentsSnap = await getDocs(collection(firestore, 'students'));
        const combined = [];
        studentsSnap.docs.forEach(docSnap => {
          const sId = docSnap.id;
          const profile = docSnap.data();
          const enrollment = enrollmentsMap[sId];

          if (enrollment) {
            combined.push({
              ...profile,
              id: sId,
              classId: enrollment.classId || '',
              gradeId: enrollment.gradeId || '',
              status: profile.status || 'Còn đi học'
            });
          }
        });

        setStudentsList(combined);
      } catch (err) {
        console.error("Lỗi khi tải dữ liệu thiếu nhi từ Firestore:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // 2. TẢI ĐIỂM TRUNG BÌNH CẢ NĂM
  useEffect(() => {
    if (!selectedScoreYearId) return;

    const fetchYearlyScores = async () => {
      setLoadingScores(true);
      try {
        const enrollmentsSnap = await getDocs(
          collection(firestore, `academic_years/${selectedScoreYearId}/enrollments`)
        );

        const newScoresMap = {};
        enrollmentsSnap.docs.forEach(d => {
          const data = d.data();
          const studentId = data.studentId || d.id;
          const yearlyObj = data.transcript?.yearly || {};
          const rawScore = yearlyObj.average ?? yearlyObj.dtb ?? yearlyObj.averageScore ?? yearlyObj.score;

          if (rawScore !== undefined && rawScore !== null && rawScore !== '') {
            const numScore = parseFloat(rawScore);
            newScoresMap[studentId] = isNaN(numScore) ? rawScore : numScore.toFixed(1);
          } else {
            newScoresMap[studentId] = null;
          }
        });

        setScoresMap(newScoresMap);
      } catch (err) {
        console.error('Lỗi khi tải điểm trung bình niên khóa:', err);
      } finally {
        setLoadingScores(false);
      }
    };

    fetchYearlyScores();
  }, [selectedScoreYearId]);

  const classOptions = useMemo(() => {
    const options = [{ value: '', label: '-- Chưa phân lớp --' }];
    currentYearClasses.forEach((c) => {
      options.push({ value: c.id, label: c.name || c.id });
    });
    return options;
  }, [currentYearClasses]);

  // ====================================================================
  // 🌟 CẬP NHẬT LỚP HỌC VÀ LƯU LỊCH SỬ CHUYỂN LỚP VÀO FIRESTORE 🌟
  // ====================================================================
  const handleUpdateStudentClass = async (studentId, newClassId) => {
    if (!currentYearId) {
      alert('Không xác định được Niên khóa hiện tại.');
      return;
    }

    const currentStudent = studentsList.find(s => s.id === studentId);
    const oldClassId = currentStudent?.classId || '';
    if (oldClassId === newClassId) return;

    setIsUpdatingClass((prev) => ({ ...prev, [studentId]: true }));
    try {
      const selectedClassObj = currentYearClasses.find(c => c.id === newClassId);
      const targetGradeId = selectedClassObj ? selectedClassObj.gradeId : '';

      const oldClassObj = currentYearClasses.find(c => c.id === oldClassId);
      const oldClassName = oldClassObj ? oldClassObj.name : (oldClassId || 'Chưa phân lớp');
      const newClassName = selectedClassObj ? selectedClassObj.name : (newClassId || 'Chưa phân lớp');

      // 1. Cập nhật phân lớp vào enrollments
      const enrollmentRef = doc(firestore, `academic_years/${currentYearId}/enrollments`, studentId);
      await setDoc(enrollmentRef, {
        classId: newClassId,
        gradeId: targetGradeId
      }, { merge: true });

      // 2. 🌟 Ghi log lịch sử chuyển lớp vào Firestore
      const historyColRef = collection(firestore, `academic_years/${currentYearId}/class_transfer_history`);
      const adminName = localStorage.getItem('catechistFullName') || localStorage.getItem('username') || 'Admin';

      await addDoc(historyColRef, {
        studentId: studentId,
        holyName: currentStudent?.holyName || '',
        fullName: currentStudent?.fullName || studentId,
        fromClassId: oldClassId,
        fromClassName: oldClassName,
        fromGradeId: currentStudent?.gradeId || oldClassObj?.gradeId || '',
        toClassId: newClassId,
        toClassName: newClassName,
        toGradeId: targetGradeId,
        changedAt: Timestamp.now(),
        changedBy: adminName,
        isReverted: false,
        revertedAt: null,
        revertedBy: null
      });

      // 3. Cập nhật state cục bộ
      setStudentsList(prevList =>
        prevList.map(s => s.id === studentId ? { ...s, classId: newClassId, gradeId: targetGradeId } : s)
      );

    } catch (error) {
      console.error('Lỗi khi cập nhật lớp học:', error);
      alert('Lỗi khi cập nhật lớp: ' + error.message);
    } finally {
      setIsUpdatingClass((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const handleUpdateStudentStatus = async (studentId, newStatus) => {
    setIsUpdatingStatus((prev) => ({ ...prev, [studentId]: true }));
    try {
      const studentRef = doc(firestore, 'students', studentId);
      await updateDoc(studentRef, { status: newStatus });

      setStudentsList(prevList =>
        prevList.map(s => s.id === studentId ? { ...s, status: newStatus } : s)
      );
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái thiếu nhi:', error);
      alert('Lỗi khi cập nhật trạng thái: ' + error.message);
    } finally {
      setIsUpdatingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const filteredStudents = useMemo(() => {
    if (!studentsList || studentsList.length === 0) return [];

    return studentsList.filter((student) => {
      const matchSidebarClass = (!selectedClassForOutlet || selectedClassForOutlet === 'all')
        ? true
        : student.classId === selectedClassForOutlet;

      if (!matchSidebarClass) return false;

      const term = searchTerm.toLowerCase().trim();
      if (!term) return true;

      const classNameObj = currentYearClasses.find(c => c.id === student.classId);
      const displayClassName = classNameObj ? classNameObj.name : student.classId;

      return (
        (student.id || '').toLowerCase().includes(term) ||
        (student.holyName || '').toLowerCase().includes(term) ||
        (student.fullName || '').toLowerCase().includes(term) ||
        (student.status || '').toLowerCase().includes(term) ||
        (displayClassName || '').toLowerCase().includes(term)
      );
    });
  }, [studentsList, selectedClassForOutlet, searchTerm, currentYearClasses]);

  const sortedStudents = useMemo(() => {
    if (!sortColumn) return filteredStudents;

    const getLastName = (name) => {
      if (!name) return '';
      const parts = name.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };

    return [...filteredStudents].sort((a, b) => {
      let valA, valB;

      if (sortColumn === 'fullName') {
        valA = getLastName(a.fullName);
        valB = getLastName(b.fullName);
      } else if (sortColumn === 'yearlyScore') {
        valA = scoresMap[a.id] ? parseFloat(scoresMap[a.id]) : -1;
        valB = scoresMap[b.id] ? parseFloat(scoresMap[b.id]) : -1;
      } else {
        valA = a[sortColumn] || '';
        valB = b[sortColumn] || '';
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredStudents, sortColumn, sortDirection, scoresMap]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const getScoreBadgeClass = (scoreVal) => {
    if (scoreVal === null || scoreVal === undefined) return 'score-none';
    const num = parseFloat(scoreVal);
    if (isNaN(num)) return 'score-none';
    if (num >= 8.0) return 'score-excellent';
    if (num >= 6.5) return 'score-good';
    if (num >= 5.0) return 'score-average';
    return 'score-weak';
  };

  return (
    <div className="student-manager-container">
      {/* Header */}
      <div className="sm-header">
        <div>
          <h2 className="sm-title">Quản Lý Thiếu Nhi</h2>
          {currentYearName && (
            <p className="sm-subtitle">
              ⭐ Danh sách phân lớp thuộc Niên Khóa: <strong>{currentYearName}</strong>
            </p>
          )}
        </div>

        <div className="sm-toolbar">
          {/* 🌟 NÚT XEM LỊCH SỬ CHUYỂN LỚP 🌟 */}
          <button
            type="button"
            onClick={() => navigate('/admin-management/class-transfer-history')}
            className="sm-btn-history"
            title="Xem toàn bộ lịch sử chuyển lớp và hoàn tác"
          >
            📜 Lịch Sử Chuyển Lớp
          </button>

          <input
            type="text"
            placeholder="🔎 Tìm theo Mã ID, Tên Thánh, Họ và Tên..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sm-search-input"
          />
          <span className="sm-count-badge">
            Sĩ số: <strong>{filteredStudents.length}</strong> em
          </span>
        </div>
      </div>

      {/* Bảng Dữ Liệu */}
      <div className="sm-table-wrapper">
        <table className="sm-table">
          <thead>
            <tr>
              <th>STT</th>
              <th onClick={() => handleSort('id')}>Mã ID {getSortIcon('id')}</th>
              <th onClick={() => handleSort('holyName')}>Tên Thánh {getSortIcon('holyName')}</th>
              <th onClick={() => handleSort('fullName')} className="sm-th-name">
                Họ và Tên {getSortIcon('fullName')}
              </th>
              <th onClick={() => handleSort('gender')}>Giới Tính {getSortIcon('gender')}</th>
              <th onClick={() => handleSort('classId')}>Phân Công Lớp Học {getSortIcon('classId')}</th>

              {/* ĐTB CẢ NĂM */}
              <th onClick={() => handleSort('yearlyScore')} className="sm-th-score">
                <div className="sm-score-header-content">
                  <span>ĐTB Cả Năm {getSortIcon('yearlyScore')}</span>
                  <select
                    value={selectedScoreYearId}
                    onChange={(e) => setSelectedScoreYearId(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="sm-select-score-year"
                    title="Chọn niên khóa để xem điểm trung bình"
                  >
                    {academicYearsList.map(y => (
                      <option key={y.id} value={y.id}>
                        {y.name} {y.isCurrent ? '(Hiện tại)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </th>

              <th onClick={() => handleSort('status')}>Trạng Thái Hồ Sơ {getSortIcon('status')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="sm-empty-cell">
                  ⏳ Đang tải dữ liệu thiếu nhi...
                </td>
              </tr>
            ) : sortedStudents.length > 0 ? (
              sortedStudents.map((student, index) => {
                const scoreVal = scoresMap[student.id];
                return (
                  <tr key={student.id} className={index % 2 === 0 ? 'sm-tr-even' : ''}>
                    <td className="sm-text-center">{index + 1}</td>
                    <td className="sm-td-code">{student.id}</td>
                    <td>{student.holyName || '-'}</td>
                    <td className="sm-td-name">{student.fullName}</td>
                    <td className="sm-text-center">
                      <span className={`sm-gender-tag ${student.gender}`}>
                        {student.gender === 'nam' ? 'Nam' : student.gender === 'nu' ? 'Nữ' : 'N/A'}
                      </span>
                    </td>
                    <td>
                      {/* Đổi Lớp Học */}
                      <select
                        value={student.classId || ''}
                        onChange={(e) => handleUpdateStudentClass(student.id, e.target.value)}
                        disabled={isUpdatingClass[student.id]}
                        className="sm-select-inline"
                      >
                        {classOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>

                    {/* HIỂN THỊ ĐIỂM TRUNG BÌNH CẢ NĂM */}
                    <td className="sm-text-center">
                      {loadingScores ? (
                        <span className="sm-score-loading">...</span>
                      ) : (
                        <span className={`sm-score-badge ${getScoreBadgeClass(scoreVal)}`}>
                          {scoreVal !== null && scoreVal !== undefined ? scoreVal : '-'}
                        </span>
                      )}
                    </td>

                    <td>
                      {/* Đổi Trạng Thái Hồ Sơ Gốc */}
                      <select
                        value={student.status || 'Còn đi học'}
                        onChange={(e) => handleUpdateStudentStatus(student.id, e.target.value)}
                        disabled={isUpdatingStatus[student.id]}
                        className={`sm-select-inline sm-status-select ${student.status === 'Nghỉ luôn' ? 'status-nghi' :
                            student.status === 'Tốt nghiệp' ? 'status-totnghiep' : 'status-conhoc'
                          }`}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="sm-empty-cell">
                  {searchTerm ? 'Không tìm thấy thiếu nhi nào phù hợp với từ khóa.' : 'Không có thiếu nhi nào trong lớp được chọn.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentManager;